import React, { createContext, useEffect, useRef, useState } from 'react';
import { AppSettingContext } from '../components/Application';
import GiftCardDetails from '../components/giftCard/GiftCardDetails';
import StepsProgressBar from '../components/giftCard/buy-giftcard/shared/StepsProgressBar';
import SummarySection from '../components/giftCard/buy-giftcard/shared/SummarySection';
import ChooseCardSubpage from '../components/giftCard/buy-giftcard/subpages/ChooseCardSubpage';
import CustomiseCardSubpage from '../components/giftCard/buy-giftcard/subpages/CustomiseCardSubpage';
import ReviewAndPaySubpage from '../components/giftCard/buy-giftcard/subpages/ReviewAndPaySubpage';
import ErrorComponent from '../components/shared/ErrorComponent';
import LoadingComponent from '../components/shared/LoadingComponent';
import * as GlobalConstants from "../constants/global";
import { findLinkByAlias } from '../helpers/GiftCardHelper';
import { isNullOrWhiteSpace } from '../helpers/Utils';
import { BuyGiftCardModel, IBuyGiftCardModel } from '../models/buy-giftcard/BuyGiftCard.model';
import { IChooseCardSubpageModel } from '../models/buy-giftcard/choose-card/ChooseCardSubpage.model';
import { IChooseCardStepModel } from '../models/buy-giftcard/choose-card/ChooseCardsStep.model';
import { IChooseRecipientsStepModel } from '../models/buy-giftcard/choose-card/ChooseRecipientsStep.model';
import { ICustomiseCardStepModel } from '../models/buy-giftcard/customise-card/CustomiseCardStep.model';
import { ICustomiseCardSubpageModel } from '../models/buy-giftcard/customise-card/CustomiseCardSubpage.model';
import { IReviewAndPaySenderInfoStepModel } from '../models/buy-giftcard/review-pay/ReviewAndPaySenderInfoStep.model';
import { IReviewAndPaySubpageModel } from '../models/buy-giftcard/review-pay/ReviewAndPaySubpage.model';
import { IReviewAndPayWhenToSendStepModel } from '../models/buy-giftcard/review-pay/ReviewAndPayWhenToSendStep.model';
import { BuyGiftCardStateEnum } from '../models/enums/BuyGiftCardStateEnum';
import { BuyGiftCardSubpageEnum } from '../models/enums/BuyGiftCardSubpageEnum';
import DataService from '../services/DataService.service';
import './BuyGiftCardPage.scss';

export const BuyGiftCardContext = createContext({} as BuyGiftCardModel);

const BuyGiftCardPageNew = () => {
    const { labels, links } = React.useContext(AppSettingContext);
    const firstRenderRef = useRef(true);
    const pageRef = useRef(null);

    const defaultPersonalMessageText = labels.get("GiftCard.App.Steps.Customise.PersonalMessage.DefaultText");

    //#region STATE PROPERTIES

    const [pageModel, setPageModel] = useState<IBuyGiftCardModel>({} as IBuyGiftCardModel);
    const [culture, setCulture] = useState('en-GB');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [previewData, setPreviewData] = useState({
        initialTemplateUrl: '',
        templateUrl: '',
        mediaUrl: '',
        personalMessage: ''
    });
    const [finalAvailableSubpage, setFinalAvailableSubpage] = useState<BuyGiftCardSubpageEnum>(BuyGiftCardSubpageEnum.CHOOSE_CARD);
    const [stateBeforeQuantityChange, setStateBeforeQuantityChange] = useState({
        wasAhead: false,
        subpage: BuyGiftCardSubpageEnum.CHOOSE_CARD
    });
    const [hasToSaveBasket, setHasToSaveBasket] = useState<boolean>(true);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;

            setIsLoading(true);
            setCulture(document.documentElement.lang);
            const basketId = localStorage.getItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID) ?? "";

            DataService.getBuyGiftCardBasket(culture, basketId, true)
                .then(response => {
                    if (response && response.id && response.id.trim()) {
                        localStorage.setItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID, response.id);
                        const model = BuyGiftCardModel.MapModel(response as IBuyGiftCardModel);

                        if (model.currentState === BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT) {
                            setHasToSaveBasket(false);
                        }

                        if (model.customiseCardSubpage.customiseCard.giftCardUrl) {
                            setPreviewData({
                                ...previewData,
                                initialTemplateUrl: model.customiseCardSubpage.customiseCard.giftCardUrl,
                                mediaUrl: model.customiseCardSubpage.customiseMail.mediaUrl ?? ""
                            });
                            setPageModel(model);
                            setFinalAvailableSubpage(model.currentSubpage);
                        } else {
                            DataService.getGiftCardsTemplates(culture)
                                .then(response => {
                                    if (response) {
                                        if (response && response.length > 0) {
                                            model.customiseCardSubpage.customiseCard.giftCardUrl = response[0]?.imageUrl ?? "";

                                            setPreviewData({
                                                ...previewData,
                                                initialTemplateUrl: response[0]?.imageUrl ?? ""
                                            });
                                            setPageModel(model);
                                        }
                                    }
                                }).catch(error => {
                                    console.error(error);
                                });
                        }
                    } else {
                        setError("API response is an invalid buy gift card model");
                    }
                }).catch(error => {
                    console.error(error);
                });
        }
    }, []);

    useEffect(() => {
        if (pageModel && pageModel.id && pageModel.id.trim()) {
            //console.log('PAGE MODEL:');
            //console.log(pageModel);

            if (!previewData.templateUrl) {
                setPreviewData({
                    ...previewData,
                    templateUrl: pageModel?.customiseCardSubpage?.customiseCard?.giftCardUrl ?? ""
                });
            }

            if (!previewData.mediaUrl) {
                setPreviewData({
                    ...previewData,
                    mediaUrl: pageModel?.customiseCardSubpage?.customiseMail?.mediaUrl ?? ""
                });
            }

            if (!previewData.personalMessage) {
                setPreviewData({
                    ...previewData,
                    personalMessage: pageModel?.customiseCardSubpage?.customiseMail?.personalMessage ?? ""
                });
            }

            setIsLoading(false);
            setTimeout(() => {
                setIsSaving(false);
            }, 500);
        }
    }, [pageModel]);

    useEffect(() => {
        // if active subpage changes, scroll to the top
        scrollToTop();
    }, [pageModel.currentSubpage]);

    useEffect(() => {
        if (hasToSaveBasket && finalAvailableSubpage === BuyGiftCardSubpageEnum.REVIEW_PAY) {
            // the user was on review and pay page, but returned to change something
            // he has to post basket to UvServices again, send him directly to the step which posts basket
            setPageModel({
                ...pageModel,
                currentState: BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND
            });
        }
    }, [hasToSaveBasket]);

    //#endregion

    //#region COMMON

    const scrollToTop = (): void => {
        const element = pageRef.current;
        if (element) {
            const topPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offset = 150; // how many pixels from the top to scroll to

            setTimeout(() => {
                window.scrollTo({
                    top: topPosition - offset,
                    behavior: 'smooth'
                });
            }, 200);
        }
    };

    const handleUpdateState = (newSubpage: BuyGiftCardSubpageEnum, newState: BuyGiftCardStateEnum): void => {
        setPageModel({
            ...pageModel,
            currentSubpage: newSubpage,
            currentState: newState
        });
    };

    const handleProgressBarSubpageChange = (newSubpage: BuyGiftCardSubpageEnum): void => {
        if (pageModel.currentSubpage === newSubpage) {
            return;
        }

        handleUpdateState(newSubpage, pageModel.currentState);
    };

    const handleError = (message: string): void => {
        setError(message);
    };

    const postBasket = async (model: IBuyGiftCardModel): Promise<boolean> => {
        if (model.customiseCardSubpage.customiseMail.personalMessage === defaultPersonalMessageText) {
            model.customiseCardSubpage.customiseMail.personalMessage = '';
        }

        const isUpdated = await DataService.updateBuyGiftCardBasket(culture, model.id, model, true)
            .then(response => {
                if (response && response.id && response.id.trim()) {
                    //console.log(response);
                    return true;
                }

                setError("Unable to do a post basket in UVServices");
                return false;
            }).catch(error => {
                console.error(error);
                return false;
            });

        return isUpdated;
    };

    //#endregion

    //#region CHOOSE CARD SUBPAGE

    const handleCardDetailsChange = (model: IChooseCardStepModel): void => {
        if (model.quantity !== pageModel.chooseCardSubpage.chooseCardDetails.quantity &&
            finalAvailableSubpage !== BuyGiftCardSubpageEnum.CHOOSE_CARD
        ) {
            // the user was ahead in the process, but returned to change the cards quantity
            // meaning he also has to change details in recipients step
            setStateBeforeQuantityChange({
                wasAhead: true,
                subpage: finalAvailableSubpage
            });
            setFinalAvailableSubpage(BuyGiftCardSubpageEnum.CHOOSE_CARD);
        }

        if (!hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel({
            ...pageModel,
            currentState: BuyGiftCardStateEnum.CHOOSE_RECIPIENTS,
            chooseCardSubpage: {
                ...pageModel.chooseCardSubpage,
                chooseCardDetails: model
            }
        });
    };

    const handleRecipientsChange = (model: IChooseRecipientsStepModel): void => {
        if (stateBeforeQuantityChange.wasAhead) {
            // if the user was ahead and returned to change cards quantity and recipients details
            // change subpage in progress bar to be the last step he was on
            setFinalAvailableSubpage(stateBeforeQuantityChange.subpage);
            setStateBeforeQuantityChange({
                wasAhead: false,
                subpage: BuyGiftCardSubpageEnum.CHOOSE_CARD
            });
        }

        if (!hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel({
            ...pageModel,
            currentState: BuyGiftCardStateEnum.CHOOSE_SENDER,
            chooseCardSubpage: {
                ...pageModel.chooseCardSubpage,
                chooseRecipients: model
            }
        });
    };

    const handleChooseCardSubpageSubmit = async (model: IChooseCardSubpageModel): Promise<void> => {
        const updatedPageModel = {
            ...pageModel,
            currentSubpage: BuyGiftCardSubpageEnum.CUSTOMISE_CARD,
            currentState: BuyGiftCardStateEnum.CUSTOMISE_CARD,
            chooseCardSubpage: model
        };

        if (!hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel(updatedPageModel);

        // change subpage in progress bar
        if (finalAvailableSubpage === BuyGiftCardSubpageEnum.CHOOSE_CARD) {
            setFinalAvailableSubpage(BuyGiftCardSubpageEnum.CUSTOMISE_CARD);
        }

        await DataService.updateBuyGiftCardModelCache(updatedPageModel, culture);
    };

    //#endregion

    //#region CUSTOMISE CARD SUBPAGE

    const handleTemplateChange = (model: ICustomiseCardStepModel, isSubmit: boolean): void => {
        if (isSubmit && !hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel({
            ...pageModel,
            currentState: isSubmit ? BuyGiftCardStateEnum.CUSTOMISE_MAIL : BuyGiftCardStateEnum.CUSTOMISE_CARD,
            customiseCardSubpage: {
                ...pageModel.customiseCardSubpage,
                customiseCard: model
            }
        });

        setPreviewData({
            ...previewData,
            templateUrl: model.giftCardUrl
        });
    };

    const handlePersonalMessageChange = (personalMessage: string): void => {
        setPageModel({
            ...pageModel,
            customiseCardSubpage: {
                ...pageModel.customiseCardSubpage,
                customiseMail: {
                    ...pageModel.customiseCardSubpage.customiseMail,
                    personalMessage: personalMessage
                }
            }
        });

        setPreviewData({
            ...previewData,
            personalMessage: personalMessage
        });
    };

    const handleMediaChange = (mediaUrl: string): void => {
        setPageModel({
            ...pageModel,
            customiseCardSubpage: {
                ...pageModel.customiseCardSubpage,
                customiseMail: {
                    ...pageModel.customiseCardSubpage.customiseMail,
                    mediaUrl: mediaUrl,
                    addMedia: !isNullOrWhiteSpace(mediaUrl),
                    useGif: isNullOrWhiteSpace(mediaUrl) || mediaUrl.includes("giphy.com")
                }
            }
        });

        setPreviewData({
            ...previewData,
            mediaUrl: mediaUrl
        });
    };

    const handleCustomiseSubpageSubmit = async (model: ICustomiseCardSubpageModel): Promise<void> => {
        const updatedPageModel = {
            ...pageModel,
            currentSubpage: BuyGiftCardSubpageEnum.REVIEW_PAY,
            currentState: BuyGiftCardStateEnum.REVIEW_PAY_SENDER_INFO,
            customiseCardSubpage: model
        };

        if (!hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel(updatedPageModel);
        await DataService.updateBuyGiftCardModelCache(updatedPageModel, culture);

        // change subpage in progress bar
        setFinalAvailableSubpage(BuyGiftCardSubpageEnum.REVIEW_PAY);
    };

    //#endregion

    //#region REVIEW AND PAY SUBPAGE

    const handleReviewAndPaySenderInfoSubmit = (model: IReviewAndPaySenderInfoStepModel): void => {
        if (!hasToSaveBasket) {
            // basket has to be posted to UvServices again
            setHasToSaveBasket(true);
        }

        setPageModel({
            ...pageModel,
            currentState: BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND,
            reviewAndPaySubpage: {
                ...pageModel.reviewAndPaySubpage,
                reviewAndPaySenderInfo: model
            }
        });
    };

    const handleReviewAndPayWhenToSendSubmit = async (model: IReviewAndPayWhenToSendStepModel): Promise<void> => {
        const updatedPageModel = {
            ...pageModel,
            currentState: BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT,
            reviewAndPaySubpage: {
                ...pageModel.reviewAndPaySubpage,
                reviewAndPayWhenToSend: model
            }
        };

        setIsSaving(true);

        // post basket to UvServices; if it's successful, allow the user to continue
        const isUpdated = await postBasket(updatedPageModel);
        if (isUpdated) {
            setPageModel(updatedPageModel);
            await DataService.updateBuyGiftCardModelCache(updatedPageModel, culture);

            setHasToSaveBasket(false);
        }
    };

    const handleReviewAndPaySubpageSubmit = (model: IReviewAndPaySubpageModel): void => {
        setIsLoading(true);

        const copyModel = Object.assign({}, pageModel);
        copyModel.reviewAndPaySubpage = model;

        if (copyModel.customiseCardSubpage.customiseMail.personalMessage === defaultPersonalMessageText) {
            copyModel.customiseCardSubpage.customiseMail.personalMessage = '';
        }

        DataService.checkoutBuyGiftCard(culture, copyModel.id, copyModel, true)
            .then(response => {
                if (response && response.id && response.id.trim()) {
                    localStorage.removeItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID);
                    const pageLink = findLinkByAlias(GlobalConstants.PAGE_LINKS.BUY_GIFT_CARD_CONFIRMATION, links);

                    if (pageLink && pageLink.url.trim()) {
                        const queryString = {
                            bid: copyModel.id
                        };

                        const url = pageLink.url + "?" + new URLSearchParams(queryString).toString();
                        window.location.replace(url); //redirect to confirmation page
                    }

                    setTimeout(() => {
                        setIsLoading(false);
                    }, 5000);
                } else {
                    handleError("Unable to checkout");
                    setIsLoading(false);
                }
            }).catch(err => {
                console.error(err)
                setIsLoading(false);
            });
    };

    //#endregion

    return (
        <>
            {error ? (
                <ErrorComponent />
            ) : (
                <>
                    {isLoading ? (
                        <LoadingComponent large={true} />
                    ) : (
                        <BuyGiftCardContext.Provider value={pageModel} >
                            <div className="container" ref={pageRef}>
                                <StepsProgressBar
                                    subpage={finalAvailableSubpage}
                                    handleSubpageChange={handleProgressBarSubpageChange}
                                />

                                <div className="flex-container">
                                    <div className="forms">
                                        {pageModel.currentSubpage === BuyGiftCardSubpageEnum.CHOOSE_CARD &&
                                            <ChooseCardSubpage
                                                subpageModel={pageModel.chooseCardSubpage}
                                                isSaving={isSaving}
                                                activeStep={pageModel.currentState}

                                                handleStateChange={handleUpdateState}
                                                handleCardDetailsChange={handleCardDetailsChange}
                                                handleRecipientsChange={handleRecipientsChange}
                                                handleSubpageSubmit={handleChooseCardSubpageSubmit}
                                            />
                                        }

                                        {pageModel.currentSubpage === BuyGiftCardSubpageEnum.CUSTOMISE_CARD &&
                                            <CustomiseCardSubpage
                                                subpageModel={pageModel.customiseCardSubpage}
                                                isSaving={isSaving}
                                                activeStep={pageModel.currentState}

                                                scrollToTop={scrollToTop}
                                                handleStateChange={handleUpdateState}
                                                handleTemplateChange={handleTemplateChange}
                                                handlePersonalMessageChange={handlePersonalMessageChange}
                                                handleMediaChange={handleMediaChange}
                                                handleSubpageSubmit={handleCustomiseSubpageSubmit}
                                            />
                                        }

                                        {pageModel.currentSubpage === BuyGiftCardSubpageEnum.REVIEW_PAY &&
                                            <ReviewAndPaySubpage
                                                subpageModel={pageModel.reviewAndPaySubpage}
                                                isSaving={isSaving}
                                                activeStep={pageModel.currentState}
                                                reviewModel={{
                                                    recipients: pageModel.chooseCardSubpage.chooseRecipients.recipients,
                                                    sendBy: pageModel.chooseCardSubpage.chooseRecipients.sendBy,
                                                    totalAmount: pageModel.chooseCardSubpage?.chooseCardDetails?.quantity * pageModel.chooseCardSubpage?.chooseCardDetails?.value
                                                }}
                                                hasToSaveBasket={hasToSaveBasket}

                                                getPageModel={() => pageModel}
                                                handleError={handleError}
                                                handleStateChange={handleUpdateState}
                                                handleSenderInfoSubmit={handleReviewAndPaySenderInfoSubmit}
                                                handleWhenToSendSubmit={handleReviewAndPayWhenToSendSubmit}
                                                handleSubpageSubmit={handleReviewAndPaySubpageSubmit}
                                            />
                                        }
                                    </div>

                                    <div className="sidebar">
                                        <SummarySection
                                            cardsQuantity={pageModel.chooseCardSubpage?.chooseCardDetails?.quantity}
                                            cardsValue={pageModel.chooseCardSubpage?.chooseCardDetails?.value}
                                            recipientsCount={pageModel.chooseCardSubpage?.chooseRecipients?.recipients?.length ?? pageModel.chooseCardSubpage?.chooseCardDetails?.quantity}
                                            sendBy={pageModel.chooseCardSubpage?.chooseRecipients?.sendBy}
                                        />

                                        <GiftCardDetails
                                            isSaving={isSaving}
                                            subpage={pageModel.currentSubpage}
                                            cardValue={pageModel.chooseCardSubpage.chooseCardDetails.value}

                                            giftCardUrl={pageModel.customiseCardSubpage.customiseCard.giftCardUrl ?? ""}
                                            initialTemplateUrl={previewData.initialTemplateUrl}
                                            previewTemplateUrl={previewData.templateUrl ?? ""}
                                            previewMediaUrl={previewData.mediaUrl ?? ""}
                                            previewPersonalMessage={previewData.personalMessage ?? ""}
                                        />
                                    </div>
                                </div>
                            </div>
                        </BuyGiftCardContext.Provider >
                    )}
                </>
            )}
        </>
    );
};

export default BuyGiftCardPageNew;